import type { FuseRouteNode } from "./fuseReport";

export type PositionedFuseNode = {
  node: FuseRouteNode;
  x: number;
  y: number;
  width: number;
  /** Available caption space beyond the symbol column, e.g. a main-consumer row before its via branches. */
  captionWidth?: number;
  row: number;
  continues?: boolean;
  cableLabelY?: number;
  cableLabelWidth?: number;
  /** Ends a terminal block even if the following block has the same consumer. */
  endsConsumerBlock?: boolean;
};
export type FuseSymbolGroup = { title: string; items: PositionedFuseNode[] };

/** Groups adjacent rows in the same symbol column using model identity, never just the displayed tag. */
export function groupFuseSymbols(nodes: PositionedFuseNode[]): FuseSymbolGroup[] {
  const groups: FuseSymbolGroup[] = [];
  const active = new Map<string, FuseSymbolGroup>();
  for (const item of nodes) {
    const metadata = item.node.group;
    if (!metadata || (item.node.kind !== "switch" && item.node.kind !== "terminal")) continue;
    const column = `${item.node.kind}:${item.x}`;
    let group = active.get(column);
    const previous = group?.items[group.items.length - 1];
    if (!previous || previous.row !== item.row - 1 || previous.node.group?.id !== metadata.id) {
      group = { title: metadata.title, items: [] };
      groups.push(group);
      active.set(column, group);
    }
    group!.items.push(item);
  }
  return groups.filter((group) => group.items.length > 1);
}

/** Shares consumer labels only across adjacent rows of the same model object and route column. */
export function groupFuseConsumers(nodes: PositionedFuseNode[]): PositionedFuseNode[][] {
  const groups: PositionedFuseNode[][] = [];
  const active = new Map<number, PositionedFuseNode[]>();
  for (const item of nodes) {
    if (item.node.kind !== "consumer") continue;
    let group = active.get(item.x);
    const previous = group?.[group.length - 1];
    const identity = item.node.consumerId ?? item.node.key;
    const previousIdentity = previous?.node.consumerId ?? previous?.node.key;
    if (!previous || previous.row !== item.row - 1 || identity !== previousIdentity) {
      group = [];
      groups.push(group);
      active.set(item.x, group);
    }
    group!.push(item);
  }
  return groups.filter((group) => group.length > 1);
}
